import { useEffect, useState } from 'react';

import { css, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { wacToken } from '@/libraries/design/foundations';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';
import { getLocalStorage, setLocalStorage } from '@/utils/localstorage';

const searchIconStyle = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	backgroundColor: wacToken('color.neutral.light100'),
	width: '40px',
	height: '40px',
	padding: token('space.100'),
	borderRadius: '50%',
	border: 0,
	cursor: 'pointer',
});

const searchIconDarkThemeStyle = css({
	backgroundColor: wacToken('color.neutral.dark200'),
});

const searchIconStyleAI = css({
	justifyContent: 'flex-end',
	position: 'relative',
	transition: 'all 0.5s ease-out',
	zIndex: 2,
	marginInlineStart: '46px',
});

const svgStyle = css({
	width: '24px',
	height: '24px',
	zIndex: '1',
	color: wacToken('color.primary.black'),
	'&:hover': {
		color: wacToken('color.primary.blue'),
	},
});

const svgDarkThemeStyle = css({
	color: wacToken('color.neutral.light0'),
	'&:hover': {
		color: wacToken('color.neutral.light0'),
	},
});

const tooltipStyle = css({
	background: wacToken('color.secondary.navy'),
	color: token('color.text.inverse'),
	fontSize: '12px',
	fontStyle: 'normal',
	fontWeight: '600',
	padding: '2px 8px',
	gap: token('space.025'),
});

const popoutBackgroundStyle = css({
	position: 'absolute',
	insetInlineEnd: '0',
	backgroundColor: wacToken('color.neutral.light100'),
	color: wacToken('color.neutral.dark500'),
	padding: '10px',
	borderRadius: '40px',
	fontSize: '12px',
	fontWeight: '500',
	whiteSpace: 'nowrap',
	width: '40px',
	height: '40px',
	zIndex: 1,
	pointerEvents: 'none',
	transition: 'width 0.5s ease-in-out',
});

const expandBackgroundStyle = css({
	width: '86px',
});

const popoutBackgroundDarkThemeStyle = css({
	backgroundColor: wacToken('color.neutral.dark200'),
	color: wacToken('color.neutral.light0'),
});

const fadeInKeyframes = keyframes({
	'0%': {
		opacity: 0,
	},
	'40%': {
		opacity: 0,
	},
	'100%': {
		opacity: 1,
	},
});

const fadeOutKeyframes = keyframes({
	'0%': {
		opacity: 1,
	},
	'100%': {
		opacity: 0,
	},
});

const popOutTextStyle = css({
	animation: `${fadeOutKeyframes} 0.25s ease-in-out forwards`,
});

const popInTextStyle = css({
	animation: `${fadeInKeyframes} 0.5s ease-in-out forwards`,
});

const baseTextStyle = css({
	opacity: 0,
});

const SEARCH_AI_ANIMATION_LOCAL_STORAGE_KEY = 'search_ai_animation_played';
const SEARCH_AI_ANIMATION_PLAYED_COUNT = 'search_ai_played_count';
const DEFAULT_SEARCH_AI_ANIMATION_EXPIRY = 60 * 60 * 24; // show once every 24 hours
const MAX_SEARCH_AI_ANIMATION_EXPIRY = 60 * 60 * 24 * 365; // set expiry to 1 year for permanent
const MAX_SEARCH_AI_ANIMATION_PLAYED_COUNT = 3; // show max 3 times

const SearchButton = (props: {
	readonly buttonId?: string;
	readonly onClick: React.MouseEventHandler<HTMLButtonElement>;
	aiEnabled?: boolean;
	showAiBackground?: boolean;
}) => {
	const intl = useIntl();
	const [showSearchPopout, setShowSearchPopout] = useState(false);
	const [hasShownPopout, setHasShownPopout] = useState(false);
	const hasPlayedPopoutAnimation =
		getLocalStorage(SEARCH_AI_ANIMATION_LOCAL_STORAGE_KEY) === 'true';
	const searchAiAnimationPlayedCount = parseInt(
		getLocalStorage(SEARCH_AI_ANIMATION_PLAYED_COUNT) || '0',
	);
	const { isDarkTheme } = useTheme();
	const hasAiBackground = props.aiEnabled && props.showAiBackground !== false;

	const translated = {
		searchLabel: intl.formatMessage({
			id: 'globalnav.v2.search.label',
			defaultMessage: 'Search',
		}),
	};

	const tooltipText = props.aiEnabled
		? intl.formatMessage({
				id: 'globalnav.v2.search-bar.ai-enabled.tooltip',
				defaultMessage: 'Search or ask anything',
			})
		: null;

	useEffect(() => {
		const shouldShowSearchAiAnimation =
			hasAiBackground &&
			!showSearchPopout &&
			!hasPlayedPopoutAnimation &&
			searchAiAnimationPlayedCount < MAX_SEARCH_AI_ANIMATION_PLAYED_COUNT;
		if (shouldShowSearchAiAnimation) {
			const timeout = setTimeout(() => {
				setShowSearchPopout(true);
				setHasShownPopout(true);

				const playedCount = searchAiAnimationPlayedCount + 1;
				if (playedCount >= MAX_SEARCH_AI_ANIMATION_PLAYED_COUNT) {
					setLocalStorage(
						SEARCH_AI_ANIMATION_LOCAL_STORAGE_KEY,
						'true',
						MAX_SEARCH_AI_ANIMATION_EXPIRY,
					);
				} else {
					setLocalStorage(
						SEARCH_AI_ANIMATION_LOCAL_STORAGE_KEY,
						'true',
						DEFAULT_SEARCH_AI_ANIMATION_EXPIRY,
					); // show once every 24 hours
				}
				setLocalStorage(
					SEARCH_AI_ANIMATION_PLAYED_COUNT,
					playedCount.toString(),
					MAX_SEARCH_AI_ANIMATION_EXPIRY,
				);

				setTimeout(() => {
					setShowSearchPopout(false);
				}, 2500);
			}, 3000);

			return () => clearTimeout(timeout);
		}
		return undefined;
	}, [hasAiBackground, showSearchPopout]);

	return (
		<search css={css({ position: 'relative' })}>
			{hasAiBackground && (
				<div
					css={[
						popoutBackgroundStyle,
						isDarkTheme && popoutBackgroundDarkThemeStyle,
						showSearchPopout && expandBackgroundStyle,
					]}
				>
					<div
						css={[
							baseTextStyle,
							showSearchPopout && popInTextStyle,
							!showSearchPopout && hasShownPopout && popOutTextStyle,
						]}
					>
						Search
					</div>
				</div>
			)}
			<Tooltip content={tooltipText} css={tooltipStyle}>
				<button
					css={[
						searchIconStyle,
						isDarkTheme && searchIconDarkThemeStyle,
						hasAiBackground && searchIconStyleAI,
					]}
					onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
						if (hasAiBackground) {
							setLocalStorage(
								SEARCH_AI_ANIMATION_LOCAL_STORAGE_KEY,
								'true',
								MAX_SEARCH_AI_ANIMATION_EXPIRY,
							);
						}
						props.onClick(e);
					}}
					id={props.buttonId}
					aria-label={String(translated.searchLabel).toLowerCase()}
					data-testid="global-nav-search-icon"
					data-event="clicked"
					data-event-component="linkButton"
					data-event-container="globalNavWac"
					data-product-key="jiraSoftware"
					data-ai-enabled={props.aiEnabled}
				>
					<svg
						css={[svgStyle, isDarkTheme && svgDarkThemeStyle]}
						width="23"
						height="25"
						viewBox="0 0 23 25"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M17.0918 18.5623L20.2497 21.7201"
							stroke="currentColor"
							strokeWidth="2.75"
							strokeLinecap="round"
						/>
						<path
							d="M10.8417 19.1316C15.289 19.1316 18.8943 15.5263 18.8943 11.079C18.8943 6.63165 15.289 3.02637 10.8417 3.02637C6.39435 3.02637 2.78906 6.63165 2.78906 11.079C2.78906 15.5263 6.39435 19.1316 10.8417 19.1316Z"
							stroke="currentColor"
							strokeWidth="2.25"
						/>
					</svg>
				</button>
			</Tooltip>
		</search>
	);
};

export default SearchButton;
